import { Component, Input, ViewChild } from '@angular/core';
import { INote, Note, NoteType } from '../../../models/note';
import { CardModule } from 'primeng/card';
import { DatePipe, NgFor, NgIf, SlicePipe } from '@angular/common';
import { PanelModule } from 'primeng/panel';
import { TagModule } from 'primeng/tag';
import { DividerModule } from 'primeng/divider';
import { ButtonModule } from 'primeng/button';
import { NoteEditorComponent } from '../note-editor/note-editor.component';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { Store } from '@ngxs/store';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { INoteHandler } from './notes-handler';
import { UserState } from '../../../state/user/user-state';
import { FormsModule } from '@angular/forms';
import { MultiSelectModule } from 'primeng/multiselect';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { DialogModule } from "primeng/dialog";

export const NoteCategoryOptions: { label: string, value: NoteType }[] =
[
    { label: "Finance",   value: NoteType.Finance },
    { label: "Nurse",     value: NoteType.Nurse },
    { label: "Manager",   value: NoteType.Manager }
];

@Component({
  //selector: 'app-notes-list',
  standalone: true,
  imports: [CardModule, SlicePipe, NgIf, NgFor, PanelModule, TagModule, DividerModule, DatePipe, ButtonModule,
    NoteEditorComponent, OverlayPanelModule],
  template: '',
  styleUrl: './notes-list.component.scss'
})
export class NotesListComponent {
  @Input() notes: Note[] = [];

  categoryFilterOptions = NoteCategoryOptions;
  selectedCategoryFilter: NoteType[] = [];

  get sortedNotes() : Note[]
  {
    const filtered = this.selectedCategoryFilter.length
      ? this.notes.filter(n => n.noteTypes?.some(t => this.selectedCategoryFilter.includes(t)))
      : this.notes;

    return filtered.toSorted((a,b) =>
    {
      const aSticky = this.isSticky(a) ? 1 : 0;
      const bSticky = this.isSticky(b) ? 1 : 0;
      if (aSticky !== bSticky)
        return bSticky - aSticky;
      return new Date(b.createDate).valueOf() - new Date(a.createDate).valueOf();
    });
  }
  set sortedNotes(val: Note[]) {
    this.notes = val;
  }
  expandedNotes: boolean[] = [];

  constructor(protected _store: Store) {
  }

  isSticky(note: Note): boolean
  {
    return note.noteTypes?.includes(NoteType.Sticky) ?? false;
  }

  categoryLabel(note: Note): string | undefined
  {
    const category = note.noteTypes?.find(t => t !== NoteType.Sticky);
    return this.categoryFilterOptions.find(o => o.value === category)?.label;
  }

  //for now, only consider "Primary" note type.
  //Primary note type is defined as the first non-Sticky value in the list of types
  getPrimaryNoteTypeLabel(noteTypes?: NoteType[]): string {
    if (!noteTypes?.length) {
      return '—';
    }

    const firstNonSticky = noteTypes.find(t => t !== NoteType.Sticky);
    if (firstNonSticky === undefined) {
      return '—';
    }

    const option = NoteCategoryOptions.find(o => o.value === firstNonSticky);
    return option?.label ?? '—';
  }

  canEditNote(note: Note): boolean
  {
    const currentUser = this._store.selectSnapshot(UserState.currentUser);
    if (!currentUser || currentUser.id !== note.createdBy.id)
      return false;

    const created = new Date(note.createDate).getTime();
    return Date.now() - created < 24 * 60 * 60 * 1000;
  }

  canAddAddendum(note: Note): boolean
  {
    const created = new Date(note.createDate).getTime();
    return Date.now() - created >= 24 * 60 * 60 * 1000;
  }

}

@Component({
  selector: 'app-encounter-notes-list',
  standalone: true,
  imports: [CardModule, SlicePipe, NgIf, NgFor, PanelModule, TagModule, DividerModule, DatePipe, ButtonModule,
    NoteEditorComponent, OverlayPanelModule, NotesListComponent, TableModule, TooltipModule, FormsModule, MultiSelectModule, DialogModule],
  templateUrl: './notes-list.component.html',
  styleUrl: './notes-list.component.scss'
})
export class EncounterNotesListComponent extends NotesListComponent {
  @Input() encounter?: any;
  @Input() handler: INoteHandler | undefined = undefined;
  @ViewChild('newNoteEditor') newNoteEditor!: NoteEditorComponent;

  viewNote: Note | null = null;
  viewNoteVisible: boolean = false;

  newNoteVisible: boolean = false;

  editingNote: INote = {} as INote;
  editNoteVisible: boolean = false;

  addendumNote: Note | null = null;
  addendumVisible: boolean = false;

  private safeHtmlCache = new WeakMap<object, SafeHtml>();

  constructor(protected override _store: Store, private sanitizer: DomSanitizer) {
    super(_store);
  }

  resetNewNote() {
    this.newNoteEditor.reset();
  }

  openViewNote(note: Note): void {
    this.viewNoteVisible = true;
    this.viewNote = note;
  }

  openEditNote(note: Note): void {
    this.editingNote = note;
    this.editNoteVisible = true;
  }

  openAddendum(note: Note): void {
    this.addendumNote = note;
    this.addendumVisible = true;
  }

  async toggleSticky(note: Note): Promise<void> {
    const types = (note.noteTypes ?? []).filter(t => t !== NoteType.Sticky);
    const updated: INote = { ...note, noteTypes: this.isSticky(note) ? types : [...types, NoteType.Sticky] };
    await this.updateNote(updated);
  }

  private normalizeHtml(html: string): string {
    return html.replace(/<(p|div)><br><\/\1>/gi, '<br>');
  }

  getSafeHtml(source: { body: string } | null): SafeHtml {
    if (!source) {
      return this.sanitizer.bypassSecurityTrustHtml('');
    }

    let cached = this.safeHtmlCache.get(source);
    if (!cached) {
      const normalizedHtml = this.normalizeHtml(source.body);
      cached = this.sanitizer.bypassSecurityTrustHtml(normalizedHtml);
      this.safeHtmlCache.set(source, cached);
    }
    return cached;
  }

  async addNote(note: INote) {
    await this.handler?.addNote(note);
  }

  async updateNote(note: INote) {
    await this.handler?.updateNote((note as Note).id, note);
  }

  async deleteNote(note: INote) {
    await this.handler?.deleteNote(this.encounter.id, (note as Note).id);
  }

  async addAddendum(event: { noteId: number, body: string }) {
    await this.handler?.addAddendum(event.noteId, event.body);
  }
}
